import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { NO_MODEL } from "@/components/EquipmentLines";
import { useAuth } from "@/context/AuthContext";

const NONE = "__none__";
const NEW = "__new__";
const OTHER = "Otro";
const REASONS = ["Compra a proveedor", "Inventario inicial", "Devolución de cliente", "Garantía de proveedor", OTHER];
const EMPTY = { business_unit_code: "", type_id: "", type_name: "", classification: "equipo", unit: "Pieza", model_id: NONE, brand: "", model: "", serialized: false,
  quantity: "", location_id: "", responsible_user_id: NONE, reason: REASONS[0], other: "", reference: "", notes: "", ids: "", carrier: "" };
const roleOf = (bu, typeName) => bu !== "gps" ? null : /gps/i.test(typeName || "") && !/sim/i.test(typeName || "") ? "gps" : /sim|chip/i.test(typeName || "") ? "sim" : null;
const ID_LABEL = { gps: "IMEI, uno por línea (puedes pegar un lote)", sim: "ICCID, uno por línea. Teléfono opcional: «ICCID, número»" };
const ID_NAME = { gps: "IMEI", sim: "ICCID" };

const Field = ({ label, children }) => <label className="block space-y-1 text-sm"><span className="text-xs text-muted-foreground">{label}</span>{children}</label>;
const Pick = ({ label, value, onChange, options, testid }) => (
  <Field label={label}>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10" data-testid={testid}><SelectValue placeholder="Selecciona…" /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select></Field>
);
const Txt = ({ label, value, onChange, testid, placeholder }) => (
  <Field label={label}><Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10" data-testid={testid} /></Field>
);

const parseIds = (text) => {
  const lines = text.split("\n").map((s) => s.split(",").map((x) => x.trim())).filter((p) => p[0]);
  const seen = new Set();
  const dups = new Set(lines.map((p) => p[0]).filter((s) => (seen.has(s) ? true : (seen.add(s), false))));
  return { lines, dups: [...dups] };
};

export const ItemDialog = ({ open, onOpenChange, onSaved, items = [], locations = [], users = [] }) => {
  const { meta } = useAuth();
  const [types, setTypes] = useState([]);
  const [models, setModels] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const activeLocs = locations.filter((l) => l.active);
  useEffect(() => { if (open) { setF({ ...EMPTY, location_id: activeLocs[0]?.id || "" }); api.get("/catalog/types").then((r) => setTypes(r.data)); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setModels([]); if (f.type_id && f.type_id !== NEW) api.get("/catalog/models", { params: { type_id: f.type_id } }).then((r) => setModels(r.data)); }, [f.type_id]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const newType = f.type_id === NEW;
  const newModel = f.model_id === NEW;
  const type = types.find((t) => t.id === f.type_id);
  const typeName = newType ? f.type_name.trim() : type?.name || "";
  const mdl = models.find((m) => m.id === f.model_id);
  const match = !newType && !newModel && f.type_id ? items.find((i) => i.tracked && i.type_id === f.type_id && (i.model_id || NONE) === f.model_id) : null;
  const role = roleOf(f.business_unit_code, typeName);
  const serialized = match ? match.serialized : (!!role || f.serialized);
  const unit = match?.unit || mdl?.unit || (newType ? f.unit : type?.unit) || "Pieza";
  const { lines, dups } = useMemo(() => parseIds(f.ids), [f.ids]);
  const qty = serialized ? lines.length : Number(f.quantity) || 0;
  const errors = [
    !f.type_id && "Selecciona o crea el tipo de artículo",
    newType && !typeName && "Escribe el nombre del artículo",
    newModel && (!f.brand.trim() || !f.model.trim()) && "Captura la marca y el modelo nuevos",
    role === "gps" && f.model_id === NONE && "Selecciona o crea la marca y modelo del GPS",
    dups.length && `Identificadores repetidos: ${dups.join(", ")}`,
    serialized && !lines.length && `Captura al menos un ${ID_NAME[role] || "número de serie"}`,
    !serialized && qty <= 0 && "Indica la cantidad recibida",
    !f.location_id && "Selecciona la ubicación inicial",
    f.reason === OTHER && !f.other.trim() && "Escribe el motivo de entrada",
  ].filter(Boolean);

  const save = async () => {
    setBusy(true);
    try {
      const attrKey = role === "sim" ? "phone" : "sim_iccid";
      const reason = [f.reason === OTHER ? f.other.trim() : f.reason, f.notes.trim()].filter(Boolean).join(" · ");
      const r = await api.post("/inventory/items/receive", {
        item_id: match?.id || null,
        catalog: match ? null : { business_unit_code: f.business_unit_code, type_name: typeName, serialized,
          brand: newModel ? f.brand.trim() : mdl?.brand || "", model: newModel ? f.model.trim() : mdl?.model || "",
          classification: newType ? f.classification : null, unit: newType ? f.unit : null },
        movement: { item_id: match?.id || "-", kind: "entrada", quantity: qty, location_id: f.location_id, reason, reference: f.reference.trim(),
          responsible_user_id: f.responsible_user_id === NONE ? null : f.responsible_user_id, carrier: role === "sim" ? f.carrier.trim() : "",
          serials: serialized ? lines.map((p) => p[0]) : [], serial_attrs: Object.fromEntries(lines.filter((p) => p[1]).map((p) => [p[0], { [attrKey]: p[1] }])) },
      });
      toast.success(`${r.data.created ? "Artículo dado de alta. " : ""}${qty} ${unit} registrada(s) en inventario`); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[95vw] overflow-y-auto overflow-x-hidden sm:max-w-lg" data-testid="inv-item-dialog">
        <DialogHeader><DialogTitle className="font-display">Agregar artículo al inventario</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Pick label="Unidad de negocio" value={f.business_unit_code} onChange={(v) => setF({ ...f, business_unit_code: v, type_id: "", model_id: NONE })} testid="inv-item-bu"
            options={(meta?.business_units || []).map((b) => ({ value: b.code, label: b.name }))} />
          {f.business_unit_code && <CatalogFields f={f} set={set} setF={setF} meta={meta} models={models} newType={newType} newModel={newModel}
            types={types.filter((t) => t.business_unit_code === f.business_unit_code && t.active)} />}
          {match && <div className="rounded-md border border-sky-500/40 p-2 text-xs text-sky-300" data-testid="inv-item-exists">
            Este artículo ya existe en inventario (existencia {match.on_hand} {match.unit}). Las piezas se agregarán a él, sin duplicarlo.</div>}
          {f.type_id && !match && !role && <div className="flex items-center justify-between gap-3"><span className="text-sm">Cada pieza tiene número de serie</span>
            <Switch checked={f.serialized} onCheckedChange={(v) => set("serialized", v)} data-testid="inv-item-serialized" /></div>}
          {f.type_id && <ReceiveFields f={f} set={set} role={role} serialized={serialized} unit={unit} lines={lines} locations={activeLocs} users={users} />}
          {f.type_id && errors.length > 0 && <ul className="space-y-0.5 text-xs text-amber-400" data-testid="inv-receive-errors">{errors.map((e) => <li key={e}>{e}</li>)}</ul>}
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={errors.length > 0 || busy} data-testid="inv-item-save">{busy ? "Guardando…" : `Registrar${qty ? ` ${qty} ${unit}` : ""}`}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CatalogFields = ({ f, set, setF, meta, types, models, newType, newModel }) => (
  <>
    <Pick label="Tipo de artículo" value={f.type_id} onChange={(v) => setF((s) => ({ ...s, type_id: v, model_id: NONE }))} testid="inv-item-type"
      options={[...types.map((t) => ({ value: t.id, label: t.name })), { value: NEW, label: "+ Nuevo tipo de artículo…" }]} />
    {newType && <div className="space-y-3 rounded-md border border-border p-3">
      <Txt label="Nombre o descripción del artículo" value={f.type_name} onChange={(v) => set("type_name", v)} testid="inv-new-type-name" placeholder="Ej. Cable calibre 18, Cámara IP…" />
      <div className="grid grid-cols-2 gap-2">
        <Pick label="Clasificación" value={f.classification} onChange={(v) => set("classification", v)} testid="inv-new-type-class"
          options={(meta?.item_classes || []).map((c) => ({ value: c.code, label: c.name }))} />
        <Pick label="Unidad de medida" value={f.unit} onChange={(v) => set("unit", v)} testid="inv-new-type-unit" options={(meta?.units || ["Pieza"]).map((u) => ({ value: u, label: u }))} />
      </div></div>}
    {f.type_id && <Pick label="Marca y modelo" value={f.model_id} onChange={(v) => set("model_id", v)} testid="inv-item-model"
      options={[{ value: NONE, label: NO_MODEL }, ...models.filter((m) => m.active).map((m) => ({ value: m.id, label: `${m.brand} ${m.model}` })), { value: NEW, label: "+ Nueva marca / modelo…" }]} />}
    {newModel && <div className="grid grid-cols-2 gap-2">
      <Txt label="Marca" value={f.brand} onChange={(v) => set("brand", v)} testid="inv-new-brand" />
      <Txt label="Modelo" value={f.model} onChange={(v) => set("model", v)} testid="inv-new-model" /></div>}
  </>
);

const ReceiveFields = ({ f, set, role, serialized, unit, lines, locations, users }) => (
  <div className="space-y-3 border-t border-border pt-3">
    {role === "sim" && <Txt label="Compañía telefónica" value={f.carrier} onChange={(v) => set("carrier", v)} testid="inv-receive-carrier" />}
    {serialized ? <Field label={ID_LABEL[role] || "Números de serie, uno por línea (puedes pegar un lote)"}>
      <Textarea rows={4} value={f.ids} onChange={(e) => set("ids", e.target.value)} className="font-mono text-xs" data-testid="inv-receive-ids" />
      <span className="text-xs text-muted-foreground" data-testid="inv-receive-id-count">{lines.length} {unit} (una por identificador)</span></Field>
      : <Field label={`Cantidad recibida (${unit})`}>
        <Input type="number" min={0} value={f.quantity} onChange={(e) => set("quantity", e.target.value)} className="h-10" data-testid="inv-receive-qty" /></Field>}
    <Pick label="Ubicación inicial" value={f.location_id} onChange={(v) => set("location_id", v)} testid="inv-receive-location"
      options={locations.map((l) => ({ value: l.id, label: l.path }))} />
    <Pick label="Responsable inicial (opcional)" value={f.responsible_user_id} onChange={(v) => set("responsible_user_id", v)} testid="inv-receive-responsible"
      options={[{ value: NONE, label: "Sin responsable" }, ...users.map((u) => ({ value: u.id, label: u.name }))]} />
    <Pick label="Motivo de entrada" value={f.reason} onChange={(v) => set("reason", v)} testid="inv-receive-reason" options={REASONS.map((r) => ({ value: r, label: r }))} />
    {f.reason === OTHER && <Input value={f.other} onChange={(e) => set("other", e.target.value)} placeholder="Describe el motivo" className="h-10" data-testid="inv-receive-reason-other" />}
    <Txt label="Referencia (factura, remisión…) — opcional" value={f.reference} onChange={(v) => set("reference", v)} testid="inv-receive-reference" />
    <Field label="Observaciones — opcional"><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="inv-receive-notes" /></Field>
  </div>
);
